"use client";

import { useLocale, useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { GlobeAltIcon, ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";

const FLAGS = { fr: "🇫🇷", nl: "🇳🇱", en: "🇬🇧" };

export default function LanguageSwitcher({ variant = "dark" }) {
  const locale = useLocale();
  const t = useTranslations("LanguageSwitcher");
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const switchTo = (nextLocale) => {
    setOpen(false);
    // `useParams()` (native Next.js) includes the current `locale` segment
    // itself, since the whole public site lives under `/[locale]/...`. If we
    // forward that stale `locale` value back into `params`, next-intl's
    // router ends up treating it as a literal path segment on top of the
    // `{ locale: nextLocale }` we pass explicitly — producing a doubled
    // prefix like `/en/nl/contact` on every switch after the first one.
    const { locale: _currentLocale, ...restParams } = params || {};
    router.replace({ pathname, params: restParams }, { locale: nextLocale });
    // `NextIntlClientProvider` lives in the ROOT layout (above the `[locale]`
    // segment), so a plain client-side `replace()` swaps the URL but Next.js
    // reuses the already-rendered root layout instead of re-invoking
    // `getLocale()`/`getMessages()` — the page silently keeps rendering the
    // previous language. `refresh()` forces a full server-component refetch
    // for the new URL, which re-runs the root layout with the new locale.
    router.refresh();
  };

  const isDark = variant === "dark";

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("label")}
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[13px] font-bold uppercase tracking-wide transition ${
          isDark
            ? "border-white/15 bg-white/10 text-white hover:bg-white/15"
            : "border-ink/12 bg-ink/5 text-ink hover:bg-ink/10"
        }`}
      >
        <GlobeAltIcon className="h-4 w-4" />
        <span>{locale}</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[170px] overflow-hidden rounded-2xl border border-black/10 bg-white shadow-premium">
          {routing.locales.map((loc) => (
            <button
              key={loc}
              type="button"
              onClick={() => switchTo(loc)}
              className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-semibold transition ${
                loc === locale
                  ? "bg-sand text-ink"
                  : "text-neutral-600 hover:bg-sand hover:text-ink"
              }`}
            >
              <span className="text-base leading-none">{FLAGS[loc]}</span>
              <span className="flex-1">{t(loc)}</span>
              {loc === locale && (
                <CheckIcon className="h-4 w-4 shrink-0 text-cta-700" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
